import React, { useCallback } from 'react';
import './HandNotesTable.css';

interface Player {
  id: number;
  position: string;
  isActive: boolean;
  stackSize: number;
}

interface HandNotesTableProps {
  players: Player[];
  onPlayerClick: (playerId: number) => void;
  /** Id of the player whose turn it is to act */
  currentTurnPlayerId: number | null;
  lastActions: Record<number, Array<{ action: string; amount?: number; number: number }>>;
  /** The table position of the hero (e.g., 'sb', 'bb', etc.) */
  heroPosition?: string;
  /** Callback to cancel (remove) last action for a player */
  onCancelAction?: (playerId: number) => void;
  /** JSX for rendering board cards */
  board?: React.ReactNode;
  /** Current pot size (will be shown under board) */
  potSize?: number;
}

// Memoized Seat component to prevent unnecessary re-renders
const Seat: React.FC<{ player: Player; isToAct: boolean; isHero: boolean; left: number; top: number; actions?: Array<{ action: string; amount?: number; number: number }>; onClick: (id: number) => void; onCancel?: (id:number)=>void }> = React.memo(({ player, isToAct, isHero, left, top, actions, onClick, onCancel }) => {
  const handleClick = useCallback(() => {
    onClick(player.id);
  }, [onClick, player.id]);

  // If player has folded (isActive false) hide the seat entirely
  if (!player.isActive) return null;

  const lastAction = actions && actions.length > 0 ? actions[actions.length - 1] : null;

  return (
    <div
      className={`player-seat ${isToAct ? 'to-act' : ''} ${isHero ? 'hero' : ''}`} style={{left: `${left}%`, top: `${top}%`}}
      onClick={handleClick}
    >
      <div className="player-avatar">
        <span>{player.position}</span>
        <span className="stack-size">${Math.round(player.stackSize)}</span>
      </div>
      {lastAction && (
        <div className="action-badge-container">
          <div 
            className={`action-badge action-${lastAction.action.toLowerCase()}`} 
            title="Click to undo last action" 
            onClick={(e)=>{ e.stopPropagation(); onCancel && onCancel(player.id);} }
            style={{cursor:'pointer'}}
          >
            {lastAction.action.toUpperCase()}{lastAction.amount && lastAction.amount > 0 ? ` $${lastAction.amount}` : ''}
          </div>
        </div>
      )}
    </div>
  );
});

const HandNotesTable: React.FC<HandNotesTableProps> = ({ players, onPlayerClick, currentTurnPlayerId, lastActions, heroPosition, onCancelAction, board, potSize }) => {
  return (
    <div className="hand-notes-table-container">
      <div className="poker-table-visual">
        {board && (
          <div className="table-center">
            <div className="board-on-table">{board}</div>
            {potSize !== undefined && (
              <div className="pot-on-table">{'$' + potSize.toFixed(2)}</div>
            )}
          </div>
        )}
        {players.map((player,index) => {
          const total = players.length;
          const heroIdx = heroPosition ? players.findIndex(p=>p.position.toLowerCase()===heroPosition.toLowerCase()) : 0;
          const rel = (index - heroIdx + total) % total;
          const angle = Math.PI + rel * (2*Math.PI/total);
          const left = 50 + 46 * Math.sin(angle);
          const top  = 50 - 56 * Math.cos(angle);
          const isHero = heroPosition && player.position.toLowerCase() === heroPosition.toLowerCase();
          return (
            <Seat
              key={player.id}
              player={player}
              isToAct={player.id === currentTurnPlayerId}
              isHero={!!isHero}
              actions={lastActions[player.id]}
              onClick={onPlayerClick}
              onCancel={onCancelAction}
              left={left}
              top={top}
            />
          );
        })}
      </div>
    </div>
  );
};

export default HandNotesTable; 